import React from "react";
import { toast } from "sonner";
import { Loader2, Route, Truck, Play, Save, AlertTriangle, CheckCircle2 } from "lucide-react";
import { adminApi } from "../../lib/api";

export default function B2BRoutesPanel() {
  const [product, setProduct] = React.useState("same_day_b2b");
  const [settlementType, setSettlementType] = React.useState("internal_driver");
  const [paidBreak, setPaidBreak] = React.useState(false);
  const [vehicleId, setVehicleId] = React.useState("");
  const [vehicles, setVehicles] = React.useState([]);
  const [pickupRoutes, setPickupRoutes] = React.useState([]);
  const [deliveryRoutes, setDeliveryRoutes] = React.useState([]);
  const [savedRoutes, setSavedRoutes] = React.useState([]);
  const [busy, setBusy] = React.useState("");
  const [engineConfig, setEngineConfig] = React.useState(null);

  const loadVehicles = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/logistics/vehicles");
      setVehicles((data.items || []).filter((v) => v.active !== false));
    } catch { /* ignore */ }
  }, []);

  const loadConfig = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/routes/engine/config");
      setEngineConfig(data);
    } catch { /* ignore */ }
  }, []);

  const loadSaved = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/routes");
      setSavedRoutes(data.items || []);
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { loadVehicles(); loadConfig(); loadSaved(); }, [loadVehicles, loadConfig, loadSaved]);

  const generatePickup = async () => {
    setBusy("pickup");
    try {
      const { data } = await adminApi().post("/admin/routes/generate-pickup", {
        product, settlement_type: settlementType, paid_break: paidBreak,
        vehicle_id: vehicleId || undefined,
      });
      setPickupRoutes(data.routes || []);
      if ((data.routes || []).length === 0) toast.info(data.message || "Ingen pakker klar");
      else toast.success(`${data.routes.length} pickup-rute(r) genereret`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke generere"); }
    finally { setBusy(""); }
  };

  const generateDelivery = async () => {
    setBusy("delivery");
    try {
      const { data } = await adminApi().post("/admin/routes/generate-delivery", {
        product, settlement_type: settlementType, paid_break: paidBreak,
        vehicle_id: vehicleId || undefined,
      });
      setDeliveryRoutes(data.routes || []);
      if ((data.routes || []).length === 0) toast.info(data.message || "Ingen pakker klar");
      else toast.success(`${data.routes.length} levering-rute(r) genereret`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke generere"); }
    finally { setBusy(""); }
  };

  const saveRoute = async (routeData) => {
    setBusy(`save-${routeData.route_type}`);
    try {
      await adminApi().post("/admin/routes", {
        route_data: routeData, product, vehicle_id: vehicleId || undefined,
      });
      toast.success("Rute gemt");
      loadSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gemning fejlede"); }
    finally { setBusy(""); }
  };

  const deleteSaved = async (id) => {
    if (!window.confirm("Slet denne rute?")) return;
    try {
      await adminApi().delete(`/admin/routes/${id}`);
      toast.success("Slettet");
      loadSaved();
    } catch { toast.error("Sletning fejlede"); }
  };

  return (
    <div data-testid="routes-panel" className="space-y-6">
      {/* Config bar */}
      <div className="bg-white border border-brand p-5">
        <h3 className="font-display font-bold text-2xl text-brand-dark mb-4">Rute-generator</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="label-flat">Produkt</label>
            <select className="input-flat" value={product} onChange={(e) => setProduct(e.target.value)} data-testid="route-product">
              <option value="same_day_b2b">Same Day Business</option>
              <option value="next_day_b2b">Next Day Business</option>
            </select>
          </div>
          <div>
            <label className="label-flat">Afregnings-type</label>
            <select className="input-flat" value={settlementType} onChange={(e) => setSettlementType(e.target.value)} data-testid="route-settlement">
              <option value="internal_driver">EMA chauffør (intern)</option>
              <option value="subcontractor">Vognmand (B2B)</option>
            </select>
          </div>
          <div>
            <label className="label-flat">Køretøj (valgfri)</label>
            <select className="input-flat" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} data-testid="route-vehicle">
              <option value="">— Ingen volumen-check —</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.usable_volume_l} L)</option>)}
            </select>
          </div>
          {settlementType === "internal_driver" && (
            <label className="text-sm flex items-end gap-2 pb-2">
              <input type="checkbox" checked={paidBreak} onChange={(e) => setPaidBreak(e.target.checked)} data-testid="route-paid-break" />
              Betalt pause
            </label>
          )}
          <div className="flex items-end gap-2 flex-wrap">
            <button onClick={generatePickup} disabled={busy === "pickup"} className="btn-primary" data-testid="route-gen-pickup">
              {busy === "pickup" ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />} Pickup
            </button>
            <button onClick={generateDelivery} disabled={busy === "delivery"} className="btn-secondary" data-testid="route-gen-delivery">
              {busy === "delivery" ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />} Levering
            </button>
          </div>
        </div>
        {engineConfig && (
          <div className="text-xs text-brand-text2 mt-3 space-y-1">
            <div>
              Motor-config: EMA-timeløn {engineConfig.internal_hourly_rate_dkk} kr · km-takst {engineConfig.km_rate_dkk} kr · Vognmand {engineConfig.subcontractor_hourly_dkk} kr/t · Pause: ≥{engineConfig.break_threshold_minutes/60}t → {engineConfig.break_duration_minutes} min · Buffer {engineConfig.buffer_time_pct}%
            </div>
            {engineConfig.traffic_light && (
              <div data-testid="engine-traffic-light-thresholds">
                Trafiklys (kr/pakke): 🟢 &lt;{engineConfig.traffic_light.green_max_dkk} · 🟡 &lt;{engineConfig.traffic_light.yellow_max_dkk} · 🔴 &lt;{engineConfig.traffic_light.red_max_dkk} · 🟥 ≥{engineConfig.traffic_light.red_max_dkk} · HUB-håndtering {engineConfig.hub_handling_cost_per_parcel_dkk} kr/pakke
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pickup routes */}
      {pickupRoutes.length > 0 && (
        <div>
          <h3 className="font-display font-bold text-xl text-brand-dark mb-3">Pickup-ruter ({pickupRoutes.length})</h3>
          {pickupRoutes.map((r, i) => <RouteCard key={i} route={r} onSave={() => saveRoute(r)} busy={busy === "save-pickup"} />)}
        </div>
      )}

      {/* Delivery routes */}
      {deliveryRoutes.length > 0 && (
        <div>
          <h3 className="font-display font-bold text-xl text-brand-dark mb-3">Levering-ruter ({deliveryRoutes.length})</h3>
          {deliveryRoutes.map((r, i) => <RouteCard key={i} route={r} onSave={() => saveRoute(r)} busy={busy === "save-delivery"} />)}
        </div>
      )}

      {/* Saved routes */}
      {savedRoutes.length > 0 && (
        <div>
          <h3 className="font-display font-bold text-xl text-brand-dark mb-3">Gemte ruter ({savedRoutes.length})</h3>
          <div className="space-y-2">
            {savedRoutes.map((sr) => (
              <div key={sr.id} className="bg-white border border-brand p-3 flex flex-wrap items-center gap-3" data-testid={`saved-route-${sr.id}`}>
                <div className="font-mono text-sm">{sr.route_number}</div>
                <div className="text-sm">{sr.route_type === "pickup" ? "🚚 Pickup" : "📦 Levering"}</div>
                <div className="text-sm text-brand-text2">{sr.data?.parcel_count} parcels · {sr.data?.distance_km} km · {sr.data?.time_breakdown?.total_minutes} min</div>
                {sr.data?.economy?.traffic_light && (
                  <span
                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 border"
                    style={{ borderColor: sr.data.economy.traffic_light.color, color: sr.data.economy.traffic_light.color }}
                    data-testid={`saved-route-tl-${sr.id}`}
                  >
                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: sr.data.economy.traffic_light.color }}></span>
                    {sr.data.economy.cost_per_parcel_dkk} kr/pakke
                  </span>
                )}
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700">{sr.status}</span>
                <button onClick={() => deleteSaved(sr.id)} className="ml-auto text-xs underline text-red-600" data-testid={`saved-route-delete-${sr.id}`}>Slet</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RouteCard({ route, onSave, busy }) {
  const t = route.time_breakdown || {};
  const s = route.settlement || {};
  const vc = route.vehicle_check;
  const sizeColor = route.route_size === "short" ? "bg-green-100 text-green-700"
    : route.route_size === "long" ? "bg-amber-100 text-amber-800"
    : "bg-red-100 text-red-700";

  return (
    <div className="bg-white border border-brand p-4 mb-3" data-testid="route-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="font-semibold text-brand-dark">
          {route.route_type === "pickup" ? "🚚 Pickup-rute" : `📦 Levering — ${route.zone_name || "Ukendt zone"}`}
        </div>
        <span className={`text-xs px-2 py-0.5 ${sizeColor}`}>{route.route_size} ({(t.total_minutes/60).toFixed(1)}t)</span>
        <span className="text-xs text-brand-text2">→ {route.suggested_driver_type?.replace(/_/g, " ")}</span>
        <button onClick={onSave} disabled={busy} className="btn-primary ml-auto" data-testid="route-save-btn">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Gem rute
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
        <Stat label={route.route_type === "pickup" ? "Kunder" : "Stops"} value={route.customer_count || route.stop_count} />
        <Stat label="Parcels" value={route.parcel_count} />
        <Stat label="Volumen" value={`${route.total_volume_l} L`} />
        <Stat label="Vægt" value={`${route.total_weight_kg} kg`} />
        <Stat label="Distance" value={`${route.distance_km} km`} />
        <Stat label="Køretid" value={`${t.drive_minutes} min`} />
        <Stat label="Total tid" value={`${t.total_minutes} min`} />
        <Stat label="Pause" value={`${t.break_minutes} min`} />
      </div>

      {/* Vehicle check */}
      {vc && (
        <div className={`p-3 mb-3 text-sm flex items-start gap-2 ${vc.blocking ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"}`} data-testid="route-vehicle-check">
          {vc.blocking ? <AlertTriangle className="text-red-600 mt-0.5 flex-shrink-0" size={18} /> : <CheckCircle2 className="text-green-600 mt-0.5 flex-shrink-0" size={18} />}
          <div className="flex-1">
            {vc.blocking ? (
              <>
                <div className="font-semibold text-red-800">{vc.warning}</div>
                <div className="text-xs text-red-700 mt-1">{vc.user_advice}</div>
              </>
            ) : (
              <div className="text-green-800">Parcels passer i køretøjet · Vol {vc.fit_volume_pct}% · Vægt {vc.fit_weight_pct}% · Antal {vc.fit_parcels_pct}%</div>
            )}
          </div>
        </div>
      )}

      {/* Settlement */}
      <div className="bg-brand-bg2 p-3 text-sm">
        <div className="font-semibold text-brand-dark mb-1">Afregning:</div>
        {route.settlement_type === "subcontractor" ? (
          <>
            <div>Vognmand: {s.approved_piecework_hours} timer × {s.hourly_rate_dkk} kr = <b>{s.total_excl_vat_dkk} kr</b> ex. moms</div>
            <div className="text-xs text-brand-text2 mt-1 italic">{s.note}</div>
          </>
        ) : (
          <>
            <div>EMA-chauffør: {s.piecework_hours} timer × {s.hourly_rate_dkk} kr = {s.piecework_pay_dkk} kr</div>
            <div>Kilometer: {s.kilometers} km × {s.km_rate_dkk} kr = {s.km_pay_dkk} kr</div>
            <div className="font-semibold mt-1">Total: {s.total_dkk} kr</div>
          </>
        )}
      </div>

      {/* Sprint 1D — Economy + traffic light + decision */}
      {route.economy && <EconomyBlock economy={route.economy} decision={route.decision} autoAssignment={route.auto_assignment} split={route.split_suggestion} />}

      {/* Stops list */}
      {route.customer_stops && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-brand-dark/80">Kunde-stops ({route.customer_stops.length})</summary>
          <ul className="mt-2 space-y-1 text-xs">
            {route.customer_stops.map((s, i) => (
              <li key={i} className="flex justify-between"><span>{i+1}. {s.business_customer_name} · {s.pickup_postal_code} {s.pickup_city}</span><span>{s.parcel_count} parcels · {s.total_volume_l} L</span></li>
            ))}
          </ul>
        </details>
      )}
      {route.delivery_stops && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-brand-dark/80">Leverings-stops ({route.delivery_stops.length})</summary>
          <ul className="mt-2 space-y-1 text-xs">
            {route.delivery_stops.map((s, i) => (
              <li key={i} className="flex justify-between"><span>{i+1}. {s.parcel_number} · {s.recipient_name} · {s.delivery_postal_code} {s.delivery_city}</span><span>{s.volume_l} L · {s.weight_kg} kg</span></li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-brand-bg2 p-2">
      <div className="text-xs text-brand-text2">{label}</div>
      <div className="font-semibold text-brand-dark">{value}</div>
    </div>
  );
}

function EconomyBlock({ economy, decision, autoAssignment, split }) {
  if (!economy || economy.error) return null;
  const tl = economy.traffic_light || {};
  const tlBg = {
    green: "bg-emerald-50 border-emerald-300",
    yellow: "bg-amber-50 border-amber-300",
    red: "bg-red-50 border-red-300",
    critical: "bg-red-100 border-red-500",
  }[tl.status] || "bg-gray-50 border-gray-200";
  const tlDot = {
    green: "bg-emerald-500",
    yellow: "bg-amber-500",
    red: "bg-red-600",
    critical: "bg-red-800",
  }[tl.status] || "bg-gray-400";
  const profitColor = economy.profit_dkk >= 0 ? "text-emerald-700" : "text-red-700";

  return (
    <div className="mt-3 space-y-3" data-testid="route-economy">
      {/* Traffic light + headline */}
      <div className={`p-3 border-2 ${tlBg} flex flex-wrap items-center gap-3`} data-testid="route-traffic-light">
        <span className={`inline-block w-4 h-4 rounded-full ${tlDot}`} aria-hidden></span>
        <div className="font-bold text-brand-dark">{tl.label}</div>
        <div className="text-sm text-brand-text2">
          {economy.cost_per_parcel_dkk} kr/pakke · Total cost {economy.total_cost_dkk} kr · Revenue {economy.revenue_dkk} kr ·
          <span className={`font-bold ml-1 ${profitColor}`}>Profit {economy.profit_dkk} kr ({economy.profit_pct}%)</span>
        </div>
      </div>

      {/* Cost breakdown grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        <CostBox label="Pickup cost/pakke" value={`${economy.pickup_cost_per_parcel_dkk} kr`} muted={economy.pickup_cost_dkk === 0} />
        <CostBox label="HUB-håndtering/pakke" value={`${economy.hub_cost_per_parcel_dkk} kr`} />
        <CostBox label="Levering cost/pakke" value={`${economy.delivery_cost_per_parcel_dkk} kr`} muted={economy.delivery_cost_dkk === 0} />
        <CostBox label="Profit/pakke" value={`${economy.profit_per_parcel_dkk} kr`} highlight={economy.profit_per_parcel_dkk >= 0 ? "good" : "bad"} />
      </div>

      {/* Primary recommendation */}
      {decision?.primary && (
        <div className="bg-blue-50 border border-blue-200 p-3 text-sm" data-testid="route-decision-primary">
          <div className="font-semibold text-blue-900">Anbefaling: {decision.primary.label}</div>
          <div className="text-xs text-blue-800 mt-0.5">{decision.primary.reason}</div>
        </div>
      )}

      {/* Auto-assignment */}
      {autoAssignment && (
        <div className="bg-purple-50 border border-purple-200 p-3 text-sm" data-testid="route-auto-assign">
          <div className="font-semibold text-purple-900">Auto-tildeling: {autoAssignment.label}</div>
          <div className="text-xs text-purple-800 mt-0.5">{autoAssignment.reason}</div>
          {autoAssignment.alternatives?.length > 0 && (
            <div className="text-xs text-purple-700 mt-1">Alternativer: {autoAssignment.alternatives.join(", ")}</div>
          )}
        </div>
      )}

      {/* Action list */}
      {decision?.actions?.length > 0 && (
        <details className="text-sm" data-testid="route-actions">
          <summary className="cursor-pointer text-brand-dark/80 font-semibold">Handlinger ({decision.actions.length})</summary>
          <ul className="mt-2 space-y-1">
            {decision.actions.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-xs">
                <span className={`px-1.5 py-0.5 rounded font-mono ${a.priority === "high" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{a.priority}</span>
                <span>{a.label}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Split suggestion */}
      {split?.splits?.length > 1 && (
        <div className="bg-orange-50 border border-orange-200 p-3 text-sm" data-testid="route-split">
          <div className="font-semibold text-orange-900">{split.message}</div>
          <ul className="text-xs mt-1 space-y-0.5">
            {split.splits.map((sp) => (
              <li key={sp.split_index}>Del {sp.split_index}: {sp.parcel_count} pakker · {sp.total_volume_l} L · {sp.total_weight_kg} kg ({sp.fit_volume_pct}% udfyldt)</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CostBox({ label, value, muted, highlight }) {
  const cls = highlight === "good" ? "bg-emerald-50 border-emerald-200"
    : highlight === "bad" ? "bg-red-50 border-red-200"
    : muted ? "bg-gray-50 border-gray-200 opacity-60"
    : "bg-white border-gray-200";
  return (
    <div className={`border p-2 ${cls}`}>
      <div className="text-xs text-brand-text2">{label}</div>
      <div className="font-semibold text-brand-dark">{value}</div>
    </div>
  );
}
